/* eslint-env browser */
// Shim for react-native-web-hooks on web.
// The real package (v3.0.2, unmaintained) uses ReactDOM.findDOMNode which
// was removed in React 19. This causes useHover/useActive/useFocus to
// silently fail (always return false), breaking hover effects on
// Button, InputField, InputSearchField, SelectionControl, Toggle.
//
// This shim provides the same hooks using ref.current directly
// (no findDOMNode needed — React 19 refs are already DOM nodes).

import { useState, useEffect } from 'react';
import { Platform } from 'react-native';

export function useHover(ref) {
  const [isHovered, setHovered] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const node = ref?.current;
    if (!node) return;

    const onEnter = () => setHovered(true);
    const onLeave = () => setHovered(false);

    node.addEventListener('mouseenter', onEnter);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      node.removeEventListener('mouseenter', onEnter);
      node.removeEventListener('mouseleave', onLeave);
    };
  }, [ref?.current]);

  return isHovered;
}

export function useActive(ref) {
  const [isActive, setActive] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const node = ref?.current;
    if (!node) return;

    const onDown = () => setActive(true);
    const onUp = () => setActive(false);

    node.addEventListener('mousedown', onDown);
    node.addEventListener('mouseup', onUp);
    document.addEventListener('mouseup', onUp, false);
    return () => {
      node.removeEventListener('mousedown', onDown);
      node.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseup', onUp, false);
    };
  }, [ref?.current]);

  return isActive;
}

export function useFocus(ref) {
  const [isFocused, setFocused] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const node = ref?.current;
    if (!node) return;

    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);

    node.addEventListener('focus', onFocus);
    node.addEventListener('blur', onBlur);
    return () => {
      node.removeEventListener('focus', onFocus);
      node.removeEventListener('blur', onBlur);
    };
  }, [ref?.current]);

  return isFocused;
}

// Re-export non-broken hooks from original package (subpath imports
// bypass the exact-match webpack alias on 'react-native-web-hooks$').
export { default as useDimensions } from 'react-native-web-hooks/build/hooks/useDimensions';
export { default as useLayout } from 'react-native-web-hooks/build/hooks/useLayout';
export { default as useScaledSize } from 'react-native-web-hooks/build/hooks/useScaledSize';
export { default as useREM } from 'react-native-web-hooks/build/hooks/useREM';
